import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import * as XLSX from "xlsx";
import { Icon } from "@iconify/react";

import {
  Autocomplete,
  Box,
  Dialog,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import "./Ticket.css";
import DistributeMethodDialog from "./DistributeMethodDialog";
import MergeTicketDialog from "./MergeTicketDialog";
import EditTicket from "../Components/EditTicket";
import ContactDetails from "../Contact/ContactDetails";
import ContactDetailsModal from "../Contact/ContactDetailsModal";
import CustomerRating from "../CustomerRating/CustomerRating";
import useWindowDimensions from "../Hooks/useWindowDimensions";
import AddTicketIcon from "../Images/AddTicket.svg";

import {
  getAgentFilter,
  getAllMaster,
  getListTicket,
} from "../Store/Actions/TicketAction";

import { SET_TABLE_PAGE } from "../Store/Types/TicketTypes";

export default function Ticket({ changePage }) {
  const { selectedTicketCaseID, tablePage } = useSelector(
    (state) => state.ticket
  );

  const [masterData, setMasterData] = useState({}); // Master data for search select options
  const [agentData, setAgentData] = useState([]);
  const [isSearchShown, setIsSearchShown] = useState(false);

  const initialParams = {
    // Range initialized as 1 day before current date
    datestart: moment().subtract(1, "day"),

    dateend: moment(),
    filteragents: "",
    status: "",
    contactby: "",
    departement: "",
    customer: "",
    title: "",
    ticketno: "",
    email: "",
    phoneno: "",
    contactref: "",
    typeid: null,
    subtypeid: null,
    subsubtypeid: null,
    skip: 0,
    take: 50,
  };

  const [searchParams, setSearchParams] = useState(initialParams);

  const initialDisplayValues = {
    agentAssign: [],
    agentSearch: [],
    contact: [],
    status: [],
    department: [],
  };

  // Display values for autocomplete components
  const [displayValues, setDisplayValues] = useState(initialDisplayValues);

  const [listTicket, setListTicket] = useState([]);

  // Table loading state
  const [isLoading, setIsLoading] = useState(true);

  // Export button loading state
  const [isExportLoading, setIsExportLoading] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [ticketMergeSource, setTicketMergeSource] = useState(null);
  const [ticketMergeDestination, setTicketMergeDestination] = useState("");

  const [isDistributeMethodDialogShown, setIsDistributeMethodDialogShown] =
    useState(false);

  const [isMergeTicketDialogShown, setIsMergeTicketDialogShown] =
    useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketsToAssign, setTicketsToAssign] = useState([]);
  const [contactID, setContactID] = useState(null);

  const [isContactDetailsModalShown, setIsContactDetailsModalShown] =
    useState(false);

  const [rowCountState, setRowCountState] = useState(0);
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [selectedRatingFormID, setSelectedRatingFormID] = useState(null);

  // Used to prevent MUI DataGrid onPageChange function to trigger when searching
  const [isSearching, setIsSearching] = useState(false);

  const dispatch = useDispatch();
  const { myInfo } = useSelector((state) => state.auth);
  const { width } = useWindowDimensions();

  // Run on first load, fetch tickets and get search options data
  useEffect(() => {
    fetchTickets();

    // Get master data for search select options
    getAllMaster()
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("master data", response.data);

          setMasterData(response.data);
        } else {
          console.log("error", response.error);
        }
      })
      .catch((err) => console.log("err", err));

    // Get agent data for search select options
    getAgentFilter(myInfo.user.AgentID)
      .then((response) => {
        if (response.status) {
          console.log("agent data", response.data);
          setAgentData(response.data);
        } else {
          console.log("error", response.error);
        }
      })
      .catch((err) => console.log("err", err));
  }, []);

  // Run everytime edit dialog or assign dialog is closed, to update ticket data, and on ticket merge
  useEffect(
    () => fetchTickets(),
    [isEditing, isDistributeMethodDialogShown, ticketMergeSource]
  );

  // Open ticket from notification
  useEffect(() => {
    if (selectedTicketCaseID) {
      setSelectedTicket(selectedTicketCaseID);
      setIsEditing(true);
    }
  }, [selectedTicketCaseID]);

  const fetchTickets = (skip) => {
    setIsLoading(true);
    let updatedSearchParams;

    if (ticketMergeSource) {
      updatedSearchParams = {
        ...searchParams,
        customerid: ticketMergeSource.CustomerID,
        subsubtypeid: ticketMergeSource.SubSubTypeID,
      };
    } else {
      updatedSearchParams = searchParams;
    }

    if (skip || skip === 0) {
      updatedSearchParams = {
        ...updatedSearchParams,
        skip: skip,
      };
    }

    getListTicket({
      ...updatedSearchParams,
      datestart: updatedSearchParams.datestart.format("YYYY-MM-DD"),
      dateend: updatedSearchParams.dateend.format("YYYY-MM-DD"),
    })
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("List ticket", response.data.ticket[0]);

          setListTicket(response.data.ticket[0]);
          setRowCountState(response.data.totaldata);
          setIsLoading(false);
          setIsSearching(false);
        } else {
          console.log("error", response.error);
          setIsLoading(false);
          setIsSearching(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsLoading(false);
        setIsSearching(false);
      });
  };

  const handleExport = () => {
    setIsExportLoading(true);

    getListTicket({
      ...searchParams,
      datestart: searchParams.datestart.format("YYYY-MM-DD"),
      dateend: searchParams.dateend.format("YYYY-MM-DD"),
      skip: 0,
      take: 999,
    })
      .then((response) => {
        if (response.status) {
          // Rows to export
          const rows = response.data.ticket[0].map((ticket) => ({
            ticketNo: ticket.TicketNo,
            title: ticket.Title,
            customer: ticket.CustomerName,
            category: ticket.Types,
            subCategory: ticket.SubType,
            detailSubCategory: ticket.SubSubType,
            channel: ticket.ContactHelpdesk,
            createdBy: ticket.CreateUserBy,

            createdDate: moment(ticket.CreatedDate).format(
              "DD-MMM-yyyy HH:mm:ss"
            ),

            assignedTo: ticket.AssignedTo,
            coordinatorName: ticket.CoordinatorName,
            lastUpdateBy: ticket.LastUpdateUser,

            lastUpdateDate: moment(ticket.LastUpdateDate).format(
              "DD-MMM-yyyy HH:mm:ss"
            ),

            status: ticket.Status,
            department: ticket.Department,
          }));

          // Create worksheet
          const worksheet = XLSX.utils.json_to_sheet(rows);

          // Create workbook
          const workbook = XLSX.utils.book_new();

          // Append worksheet to workbook, and name the worksheet "Tickets"
          XLSX.utils.book_append_sheet(workbook, worksheet, "Tickets");

          // Rename the columns
          XLSX.utils.sheet_add_aoa(
            worksheet,
            [
              [
                "Ticket No",
                "Title",
                "Customer",
                "Category",
                "Sub Category",
                "Detail Sub Category",
                "Channel",
                "Created By",
                "Created Date",
                "Assigned To",
                "Coordinator Name",
                "Last Update By",
                "Last Update Date",
                "Status",
                "Department",
              ],
            ],

            { origin: "A1" }
          );

          const columns = [
            "ticketNo",
            "title",
            "customer",
            "category",
            "subCategory",
            "detailSubCategory",
            "channel",
            "createdBy",
            "createdDate",
            "assignedTo",
            "coordinatorName",
            "lastUpdateBy",
            "lastUpdateDate",
            "status",
            "department",
          ];

          // Calculate max width for each column
          const calculateMaxWidth = (column) => {
            return rows.reduce((prev, curr) => {
              if (curr[column]) {
                return Math.max(prev, curr[column].length);
              } else {
                return 10;
              }
            }, 10);
          };

          const maxColumnWidths = columns.map((column) =>
            calculateMaxWidth(column)
          );

          worksheet["!cols"] = maxColumnWidths.map((width) => ({ wch: width }));

          // Write the file and save
          XLSX.writeFile(
            workbook,
            `Tickets ${searchParams.datestart.format("YYYY-MM-DD")} - ${searchParams.dateend.format("YYYY-MM-DD")}.xlsx`,
            { compression: true }
          );

          setIsExportLoading(false);
        } else {
          console.log("error", response.error);
          setIsExportLoading(false);
        }
      })
      .catch((err) => {
        console.log("err", err);
        setIsExportLoading(false);
      });
  };

  const columns = [
    {
      field: "TicketNo",
      flex: 1.4,
      headerName: "Ticket No",
    },

    {
      field: "Title",
      flex: 1.2,
      headerName: "Title",
    },

    {
      field: "CustomerName",
      flex: 1,
      headerName: "Customer",
      minWidth: 95,
    },

    {
      field: "Types",
      flex: 1.4,
      headerName: "Category",
    },

    {
      field: "SubType",
      flex: 1.4,
      headerName: "Sub Category",
    },

    {
      field: "SubSubType",
      flex: 1.4,
      headerName: "Detail Sub Category",
    },

    {
      align: "center",
      field: "ContactHelpdesk",
      flex: 0.8,
      headerName: "Channel",
      minWidth: 50,

      renderCell: (params) => {
        let icon;
        let color;

        switch (params.row.ContactID) {
          case 1:
            icon = "ic:baseline-local-phone";
            color = "#BA68C8";
            break;

          case 2:
            icon = "material-symbols:mail-outline";
            color = "#3F80FF";
            break;

          case 3:
            icon = "mdi:fax";
            color = "#3F80FF";
            break;

          case 5:
            icon = "bx:chat";
            color = "#FF9800";
            break;

          case 7:
            icon = "mdi:facebook";
            color = "#3F80FF";
            break;

          case 8:
            icon = "mdi:instagram";
            color = "#BA68C8";
            break;

          case 9:
          case 10:
            icon = "ic:baseline-whatsapp";
            color = "#4CAF50";
            break;

          case 18:
            icon = "fluent:video-person-call-24-filled";
            color = "#FFA500";
            break;

          default:
            icon = "ic:outline-error";
            color = "#727272";
            break;
        }

        return <Icon color={color} height="26" icon={icon} width="26" />;
      },
    },

    {
      field: "CreateUserBy",
      flex: 1,
      headerName: "Created By",
    },

    {
      field: "CreatedDate",
      flex: 1,
      headerName: "Created Date",
      renderCell: (params) =>
        moment.utc(params.row.CreatedDate).format("DD-MMM-yyyy HH:mm:ss"),
    },

    {
      field: "AssignedTo",
      flex: 1,
      headerName: "Assigned To",
      minWidth: 90,
    },

    {
      field: "CoordinatorName",
      flex: 1,
      headerName: "Coordinator Name",
      minWidth: 90,
    },

    {
      field: "LastUpdateUser",
      flex: 1,
      headerName: "Last Update By",
    },

    {
      field: "LastUpdateDate",
      flex: 1,
      headerName: "Last Update Date",
      renderCell: (params) =>
        moment.utc(params.row.LastUpdateDate).format("DD-MMM-yyyy HH:mm:ss"),
    },

    {
      field: "Status",
      flex: 1,
      headerName: "Status",
      minWidth: 50,
    },

    {
      field: "Department",
      flex: 1,
      headerName: "Department",
      minWidth: 50,
    },

    {
      flex: 1,
      headerName: "Action",
      minWidth: 100,

      renderCell: (params) => {
        // Hide action for ticket merge source
        if (params.id === ticketMergeSource?.CaseID) {
          return null;
        } else {
          return (
            <>
              <Tooltip title="View Ticket" arrow placement="left">
                <IconButton
                  onClick={() => {
                    if (ticketMergeSource) {
                      setTicketMergeDestination(params.row);
                      setIsMergeTicketDialogShown(true);
                    } else {
                      setSelectedTicket(params.row.CaseID);
                      setIsEditing(true);
                    }
                  }}
                >
                  <Icon
                    color="#7e7e7e"
                    height="26"
                    icon={
                      ticketMergeSource
                        ? "bx:link"
                        : "ic:baseline-remove-red-eye"
                    }
                    width="26"
                  />
                </IconButton>
              </Tooltip>

              {params.row.IsSendCSAT && (
                <Tooltip title="View Rating" arrow placement="left">
                  <IconButton
                    onClick={() => {
                      setSelectedRatingFormID(params.row.TicketFormCSATGuid);
                      console.log(params.row.TicketFormCSATGuid);
                    }}
                  >
                    <Icon
                      color="#7e7e7e"
                      height="26"
                      icon="material-symbols:rate-review-outline-rounded"
                      width="26"
                    />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        }
      },
    },
  ];

  return (
    <div className="Ticket">
      <div className="list_header">
        <div className="list_headerLeft">
          {contactID !== null && width <= 1200 && (
            <Icon
              icon="material-symbols:arrow-back-ios-new-rounded"
              width="32"
              height="32"
              onClick={() => {
                setContactID(null);
                setIsEditing(true);
              }}
            />
          )}

          <h1>
            {ticketMergeSource
              ? "Ticket Merge"
              : isEditing
                ? "Ticket Detail"
                : contactID
                  ? "Contact Details"
                  : "Ticket"}
          </h1>
        </div>
      </div>

      {isEditing && width >= 1200 ? (
        <EditTicket
          caseID={selectedTicket}
          setIsEditing={setIsEditing}
          setContactID={setContactID}
          setIsContactDetailsModalShown={setIsContactDetailsModalShown}
          setSelectedTicket={setSelectedTicket}
          setTicketMergeSource={setTicketMergeSource}
        />
      ) : contactID !== null ? (
        <ContactDetails
          contactID={contactID}
          setIsContactDetailsModalShown={setIsContactDetailsModalShown}
        />
      ) : (
        <div className="containerTicket">
          {/* ini yang box putih */}
          <div className="container_Header">
            {/* New search */}
            {isSearchShown || ticketMergeSource ? (
              <>
                <div className="search">
                  <div className="row">
                    <div className="single_button">
                      <Tooltip title="Close" arrow>
                        <IconButton
                          size="medium"
                          onClick={() => {
                            setIsSearchShown(false);
                            setTicketMergeSource(null);
                          }}
                        >
                          <Icon icon="ph:x-bold" width="28" height="28" />
                        </IconButton>
                      </Tooltip>
                    </div>

                    {ticketMergeSource ? (
                      <p className="merge-text">
                        Select another Ticket to link with
                      </p>
                    ) : (
                      <>
                        <div className="date-range">
                          <DatePicker
                            disableFuture
                            label="Start Date"
                            maxDate={searchParams.dateend}
                            value={searchParams.datestart}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(newValue) =>
                              setSearchParams({
                                ...searchParams,
                                datestart: newValue,
                              })
                            }
                          />

                          <Icon
                            color="#7E7E7E"
                            height="26"
                            icon="mdi:arrow-expand"
                            width="26"
                          />

                          <DatePicker
                            disableFuture
                            label="End Date"
                            minDate={searchParams.datestart}
                            value={searchParams.dateend}
                            renderInput={(params) => <TextField {...params} />}
                            onChange={(newValue) =>
                              setSearchParams({
                                ...searchParams,
                                dateend: newValue,
                              })
                            }
                          />
                        </div>

                        <TextField
                          className="ticket-no"
                          label="Ticket No"
                          value={searchParams.ticketno}
                          onChange={(event) =>
                            setSearchParams({
                              ...searchParams,
                              ticketno: event.target.value,
                            })
                          }
                        />
                      </>
                    )}
                  </div>

                  <div className="row">
                    <TextField
                      className="search-item"
                      label="Email"
                      value={searchParams.email}
                      onChange={(event) =>
                        setSearchParams({
                          ...searchParams,
                          email: event.target.value,
                        })
                      }
                    />

                    <TextField
                      className="search-item"
                      label="Phone No"
                      value={searchParams.phoneno}
                      onChange={(event) =>
                        setSearchParams({
                          ...searchParams,
                          phoneno: event.target.value,
                        })
                      }
                    />

                    {/* Temp hidden */}
                    {/* <TextField
                      className="search-item"
                      label="Contact Ref"
                      value={searchParams.contactref}
                      onChange={(event) =>
                        setSearchParams({
                          ...searchParams,
                          contactref: event.target.value,
                        })
                      }
                    /> */}

                    <div className="search-item" />
                  </div>

                  <div className="row">
                    <TextField
                      className="search-item"
                      label="Title"
                      value={searchParams.title}
                      onChange={(event) =>
                        setSearchParams({
                          ...searchParams,
                          title: event.target.value,
                        })
                      }
                    />

                    {ticketMergeSource ? (
                      <>
                        <TextField
                          className="ticket-no"
                          label="Ticket No"
                          value={searchParams.ticketno}
                          onChange={(event) =>
                            setSearchParams({
                              ...searchParams,
                              ticketno: event.target.value,
                            })
                          }
                        />

                        {/* Temp hidden */}
                        {/* <Autocomplete
                          className="search-item"
                          filterSelectedOptions
                          multiple
                          options={masterData.master_contact}
                          value={displayValues.contact}
                          getOptionLabel={(contact) => contact.Description}
                          onChange={(event, value) => {
                            setSearchParams({
                              ...searchParams,
                              contactby: value.length
                                ? value.map((val) => val.ContactID)
                                : "",
                            });

                            setDisplayValues({
                              ...displayValues,
                              contact: value,
                            });
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Contact By" />
                          )}
                        /> */}
                      </>
                    ) : (
                      <>
                        <TextField
                          className="search-item"
                          label="Customer"
                          value={searchParams.customer}
                          onChange={(event) =>
                            setSearchParams({
                              ...searchParams,
                              customer: event.target.value,
                            })
                          }
                        />

                        <div className="search-item" />
                      </>
                    )}
                  </div>

                  <div className="row">
                    {/* Temp hidden */}
                    {/* {!ticketMergeSource && (
                      <Autocomplete
                        className="search-item"
                        filterSelectedOptions
                        multiple
                        options={masterData.master_contact}
                        value={displayValues.contact}
                        getOptionLabel={(contact) => contact.Description}
                        onChange={(event, value) => {
                          setSearchParams({
                            ...searchParams,
                            contactby: value.length
                              ? value.map((val) => val.ContactID)
                              : "",
                          });

                          setDisplayValues({
                            ...displayValues,
                            contact: value,
                          });
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Contact By" />
                        )}
                      />
                    )} */}

                    <Autocomplete
                      className="search-item"
                      filterSelectedOptions
                      multiple
                      options={masterData.master_status}
                      value={displayValues.status}
                      getOptionLabel={(status) => status.Description}
                      onChange={(event, value) => {
                        setSearchParams({
                          ...searchParams,
                          status: value.length
                            ? value.map((val) => val.StatusID)
                            : "",
                        });

                        setDisplayValues({
                          ...displayValues,
                          status: value,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Status" />
                      )}
                    />

                    <Autocomplete
                      className="search-item"
                      filterSelectedOptions
                      multiple
                      options={masterData.master_departement}
                      value={displayValues.department}
                      getOptionLabel={(dept) => dept.Description}
                      onChange={(event, value) => {
                        setSearchParams({
                          ...searchParams,
                          departement: value.length
                            ? value.map((val) => val.DepartmentID)
                            : "",
                        });

                        setDisplayValues({
                          ...displayValues,
                          department: value,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Department" />
                      )}
                    />

                    <Autocomplete
                      className="search-item"
                      filterSelectedOptions
                      multiple
                      options={agentData}
                      value={displayValues.agentSearch}
                      getOptionLabel={(agent) => agent.AgentName}
                      onChange={(event, value) => {
                        setSearchParams({
                          ...searchParams,
                          filteragents: value.length
                            ? value.map((val) => val.AgentID)
                            : "",
                        });

                        setDisplayValues({
                          ...displayValues,
                          agentSearch: value,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Agent" />
                      )}
                    />

                    {ticketMergeSource && <div className="search-item" />}
                  </div>

                  <div className="row">
                    <TextField
                      className="search-item"
                      label="Category"
                      select
                      value={searchParams.typeid}
                      onChange={(e) =>
                        setSearchParams({
                          ...searchParams,
                          typeid: e.target.value,
                          subtypeid: null,
                          subsubtypeid: null,
                        })
                      }
                    >
                      {masterData.master_type
                        ?.filter((parent) => parent.ParentID === 0)
                        .map((type) => (
                          <MenuItem value={type.TypeID}>
                            {type.Description}
                          </MenuItem>
                        ))}
                    </TextField>

                    {searchParams.typeid &&
                    masterData.master_type?.filter(
                      (parent) => parent.ParentID === searchParams.typeid
                    ).length ? (
                      <TextField
                        className="search-item"
                        label="Sub Category"
                        select
                        value={searchParams.subtypeid}
                        onChange={(e) =>
                          setSearchParams({
                            ...searchParams,
                            subtypeid: e.target.value,
                            subsubtypeid: null,
                          })
                        }
                      >
                        {searchParams.typeid ? (
                          masterData.master_type
                            ?.filter(
                              (parent) =>
                                parent.ParentID === searchParams.typeid
                            )
                            .map((type) => (
                              <MenuItem value={type.TypeID}>
                                {type.Description}
                              </MenuItem>
                            ))
                        ) : (
                          <MenuItem value="">Please select a category</MenuItem>
                        )}
                      </TextField>
                    ) : (
                      <div className="search-item" />
                    )}

                    {searchParams.subtypeid &&
                    masterData.master_type?.filter(
                      (parent) => parent.ParentID === searchParams.subtypeid
                    ).length ? (
                      <TextField
                        className="search-item"
                        label="Detail Sub Category"
                        select
                        value={searchParams.subsubtypeid}
                        onChange={(e) =>
                          setSearchParams({
                            ...searchParams,
                            subsubtypeid: e.target.value,
                          })
                        }
                      >
                        {searchParams.subtypeid ? (
                          masterData.master_type
                            ?.filter(
                              (parent) =>
                                parent.ParentID === searchParams.subtypeid
                            )
                            .map((type) => (
                              <MenuItem value={type.TypeID}>
                                {type.Description}
                              </MenuItem>
                            ))
                        ) : (
                          <MenuItem value="">
                            Please select a sub category
                          </MenuItem>
                        )}
                      </TextField>
                    ) : (
                      <div className="search-item" />
                    )}
                  </div>
                </div>

                <div className="search-buttons">
                  <button
                    className="clear-button"
                    onClick={() => {
                      setSearchParams(initialParams);
                      setDisplayValues(initialDisplayValues);
                    }}
                  >
                    <Icon icon="ri:brush-3-line" width="20" height="20" />
                    Clear
                  </button>

                  <button
                    className="search-button"
                    onClick={() => {
                      setIsSearching(true);
                      setTicketsToAssign([]);

                      setSearchParams({
                        ...searchParams,
                        skip: 0,
                      });

                      dispatch({
                        type: SET_TABLE_PAGE,
                        payload: { tablePage: 0 },
                      });

                      fetchTickets(0); // 0 to reset skip value
                    }}
                  >
                    <Icon icon="mingcute:search-line" width="20" height="20" />
                    Search
                  </button>
                </div>
              </>
            ) : (
              <div className="search-button-container">
                <div className="single_button">
                  <Tooltip title="Search" arrow>
                    <IconButton
                      size="medium"
                      onClick={() => setIsSearchShown(true)}
                    >
                      <Icon
                        icon="mingcute:search-line"
                        width="28"
                        height="28"
                      />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            )}

            {!ticketMergeSource ? (
              <div
                className={`Ticket_Container_right ${
                  myInfo.user.Level <= 0 ? "no-assign" : ""
                }`}
              >
                {myInfo.user.Level > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <div className="single_button">
                      <Tooltip title="Assign Ticket" arrow>
                        <IconButton
                          size="medium"
                          onClick={() => setIsDistributeMethodDialogShown(true)}
                        >
                          <Icon
                            height="26"
                            icon="material-symbols:assignment-outline-rounded"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div className="single_button">
                    <Tooltip title="Bulk Action" arrow>
                      <IconButton
                        size="medium"
                        onClick={() => {
                          setIsBulkEditing(true);
                          setIsDistributeMethodDialogShown(true);
                        }}
                      >
                        <Icon height="26" icon="ix:document-bulk" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <div className="single_button">
                    <Tooltip title="Add Ticket" arrow>
                      <IconButton
                        size="medium"
                        onClick={() => changePage("contact")}
                      >
                        <img src={AddTicketIcon} alt="" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Box>

                {myInfo.user.Level > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <div className="single_button">
                      <Tooltip title="Export ticket" arrow>
                        <IconButton
                          disabled={isExportLoading}
                          size="medium"
                          onClick={() => handleExport()}
                        >
                          <Icon
                            icon={
                              isExportLoading
                                ? "eos-icons:loading"
                                : "ph:microsoft-excel-logo"
                            }
                            width="28"
                            height="28"
                          />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </Box>
                ) : (
                  ""
                )}
              </div>
            ) : (
              <div
                className={`Ticket_Container_right ${
                  myInfo.user.Level <= 0 ? "no-assign" : ""
                }`}
              />
            )}
          </div>

          <div className="container_Body">
            <DataGrid
              // checkboxSelection={myInfo.user.Level > 0} // Only show for higher level users
              checkboxSelection // All agent can select (for bulk edit)
              className="table"
              columns={columns}
              disableColumnFilter
              disableColumnMenu
              disableSelectionOnClick
              headerHeight={75}
              keepNonExistentRowsSelected
              loading={isLoading}
              page={tablePage}
              pageSize={50}
              paginationMode="server"
              rowCount={rowCountState}
              rows={listTicket}
              rowsPerPageOptions={[]} // hide rows per page
              selectionModel={ticketsToAssign}
              // Add classname for ticket merge source
              getRowClassName={(params) => {
                let className = "";

                if (params.id === ticketMergeSource?.CaseID) {
                  className += " merge-source";
                }

                if (params.row.IsOverSLA) {
                  className += " over-sla";
                }

                return className;
              }}
              getRowHeight={() => "auto"}
              getRowId={(row) => row.CaseID}
              onPageChange={(newPage) => {
                if (!isSearching) {
                  if (newPage > tablePage) {
                    fetchTickets(searchParams.skip + 50);

                    setSearchParams((prevState) => {
                      return {
                        ...searchParams,
                        skip: prevState.skip + 50,
                      };
                    });
                  } else {
                    fetchTickets(searchParams.skip - 50);

                    setSearchParams((prevState) => {
                      return {
                        ...searchParams,
                        skip: prevState.skip - 50,
                      };
                    });
                  }

                  dispatch({
                    type: SET_TABLE_PAGE,
                    payload: { tablePage: newPage },
                  });
                }
              }}
              onSelectionModelChange={(newTicketsToAssign) =>
                setTicketsToAssign(newTicketsToAssign)
              }
            />
          </div>
        </div>
      )}

      <Dialog
        open={isEditing && width <= 1200}
        className="dialog"
        fullWidth
        scroll="paper"
        onClose={() => setIsEditing(false)}
      >
        <EditTicket
          caseID={selectedTicket}
          setIsEditing={setIsEditing}
          setContactID={setContactID}
          setIsContactDetailsModalShown={setIsContactDetailsModalShown}
        />
      </Dialog>

      <DistributeMethodDialog
        isBulkEditing={isBulkEditing}
        isOpen={isDistributeMethodDialogShown}
        selectedTicket={ticketsToAssign}
        setIsBulkEditing={setIsBulkEditing}
        setIsDistributeMethodDialogShown={setIsDistributeMethodDialogShown}
        statusOptions={masterData.master_status}
      />

      {width <= 1200 && contactID !== null && (
        <ContactDetails
          contactID={contactID}
          setIsContactDetailsModalShown={setIsContactDetailsModalShown}
        />
      )}

      <ContactDetailsModal
        contactID={contactID}
        isOpen={isContactDetailsModalShown}
        setIsContactDetailsModalShown={setIsContactDetailsModalShown}
      />

      <MergeTicketDialog
        isOpen={isMergeTicketDialogShown}
        setIsMergeTicketDialogShown={setIsMergeTicketDialogShown}
        setTicketMergeDestination={setTicketMergeDestination}
        setTicketMergeSource={setTicketMergeSource}
        ticketMergeDestination={ticketMergeDestination}
        ticketMergeSource={ticketMergeSource}
      />

      {selectedRatingFormID && (
        <CustomerRating
          formID={selectedRatingFormID}
          onClose={() => setSelectedRatingFormID(null)}
        />
      )}
    </div>
  );
}
